import React, { useRef, useEffect, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Copy, Check, Image, Download } from "lucide-react";

const CANVAS_W = 800;
const CANVAS_H = 520;

function drawWelcomeCard(canvas, leadName, closerName) {
  const ctx = canvas.getContext("2d");
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;

  // Background gradient
  const bgGrad = ctx.createLinearGradient(0, 0, CANVAS_W, CANVAS_H);
  bgGrad.addColorStop(0, "#f8fffe");
  bgGrad.addColorStop(0.4, "#f0f7ff");
  bgGrad.addColorStop(1, "#faf5ff");
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

  // Decorative circles
  ctx.globalAlpha = 0.06;
  ctx.fillStyle = "#1D9E75";
  ctx.beginPath(); ctx.arc(160, 150, 200, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#7C3AED";
  ctx.beginPath(); ctx.arc(640, 380, 180, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#0EA5E9";
  ctx.beginPath(); ctx.arc(400, 260, 250, 0, Math.PI * 2); ctx.fill();
  ctx.globalAlpha = 1;

  // Icon square
  const iconGrad = ctx.createLinearGradient(368, 40, 432, 104);
  iconGrad.addColorStop(0, "#1D9E75");
  iconGrad.addColorStop(1, "#0EA5E9");
  roundRect(ctx, 368, 40, 64, 64, 16);
  ctx.fillStyle = iconGrad;
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.font = "bold 28px sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("B", 400, 82);

  // Greeting
  ctx.textAlign = "center";
  ctx.font = "900 42px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  const nameGrad = ctx.createLinearGradient(200, 120, 600, 160);
  nameGrad.addColorStop(0, "#111111");
  nameGrad.addColorStop(0.5, "#1D9E75");
  nameGrad.addColorStop(1, "#7C3AED");
  ctx.fillStyle = nameGrad;
  ctx.fillText(`Hey ${leadName}!`, 400, 152);

  // Subline
  ctx.font = "400 15px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  ctx.fillStyle = "#555";
  ctx.fillText(`I'm ${closerName} from BulkJobApply. Here's what we'll cover:`, 400, 186);

  // Agenda card background
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.strokeStyle = "rgba(29,158,117,0.2)";
  ctx.lineWidth = 1;
  roundRect(ctx, 120, 210, 560, 250, 16);
  ctx.fill();
  ctx.stroke();

  // Agenda title
  ctx.font = "800 11px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  ctx.fillStyle = "#1D9E75";
  ctx.textAlign = "left";
  ctx.fillText("IN THE NEXT 12 MINUTES, YOU'LL SEE", 150, 242);

  // Agenda items
  const items = [
    ["Why 97% of job seekers never hear back", "...and the one number that changes everything"],
    ["Real results from 375+ clients", "Live screenshots, real WhatsApp conversations"],
    ["The 3-system approach that gets interviews", "Applications + Networking + Recruiter Reach"],
    ["How to start getting callbacks this week", "Personalized plan based on your exact situation"],
  ];

  items.forEach((item, i) => {
    const y = 268 + i * 46;

    // Number badge
    const numGrad = ctx.createLinearGradient(150, y - 10, 174, y + 14);
    numGrad.addColorStop(0, "#1D9E75");
    numGrad.addColorStop(1, "#0EA5E9");
    roundRect(ctx, 150, y - 10, 24, 24, 6);
    ctx.fillStyle = numGrad;
    ctx.fill();
    ctx.font = "800 12px sans-serif";
    ctx.fillStyle = "#fff";
    ctx.textAlign = "center";
    ctx.fillText(`${i + 1}`, 162, y + 6);

    // Text
    ctx.textAlign = "left";
    ctx.font = "500 13px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
    ctx.fillStyle = "#333";
    ctx.fillText(item[0], 186, y + 4);

    ctx.font = "700 11px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
    ctx.fillStyle = "#1D9E75";
    ctx.fillText(item[1], 186, y + 20);
  });

  // Footer
  ctx.textAlign = "center";
  ctx.font = "400 12px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  ctx.fillStyle = "#aaa";
  ctx.fillText("Ready? Let's dive in.", 400, 490);
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

export default function WelcomeImageCard({ leadName, closerName }) {
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (canvasRef.current) {
      drawWelcomeCard(canvasRef.current, leadName, closerName);
    }
  }, [leadName, closerName]);

  const copyImage = useCallback(async () => {
    if (!canvasRef.current) return;
    try {
      const blob = await new Promise((resolve) =>
        canvasRef.current.toBlob(resolve, "image/png")
      );
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: download
      const link = document.createElement("a");
      link.download = `welcome-${leadName.replace(/\s/g, "_")}.png`;
      link.href = canvasRef.current.toDataURL("image/png");
      link.click();
    }
  }, [leadName]);

  const downloadImage = useCallback(() => {
    if (!canvasRef.current) return;
    const link = document.createElement("a");
    link.download = `welcome-${leadName.replace(/\s/g, "_")}.png`;
    link.href = canvasRef.current.toDataURL("image/png");
    link.click();
  }, [leadName]);

  return (
    <div className="welcome-img-card" data-testid="welcome-image-card">
      <div className="welcome-img-label">
        <Image className="w-4 h-4" />
        <span>Personalized welcome card for WhatsApp</span>
      </div>
      <div className="welcome-img-preview">
        <canvas ref={canvasRef} className="welcome-canvas" />
      </div>
      <div className="welcome-img-actions">
        <Button size="sm" onClick={copyImage} className="welcome-img-btn" data-testid="copy-welcome-image">
          {copied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy image</>}
        </Button>
        <Button size="sm" variant="outline" onClick={downloadImage} data-testid="download-welcome-image">
          <Download className="w-3.5 h-3.5" /> Save
        </Button>
      </div>
      <p className="welcome-img-tip">Copy or save this image, then paste it into WhatsApp along with the message above</p>
    </div>
  );
}
